// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/features/financeiro/services/contasPagarService.ts
// ════════════════════════════════════════════════════════════════════════════

import { apiClient } from '@/lib/apiClient';
import type {
  ContaPagar,
  CriarContaPagarRequest,
  AtualizarContaPagarRequest,
  ContaPagarResumoDto,
  ContaPagarParcelaDto,
  ContaPagarDetalheDto,
  ContaPagarFornecedorResumoDto,
  ContaPagarTituloGerencialDto,
  PrazoMedioPagamentoDto,
  FiltroGerencialPagar,
  BaixarContaPagarRequest,
  BaixarParcelaPagarRequest,
  BaixarParcelasPagarLoteRequest,
  BaixarLoteRequest,
  BaixaResultado,
  BaixaParcelaResultado,
  FiltroContasPagar,
  ListaFinanceiraResponse,
  PaginadoResponse,
} from '../types';

const BASE = '/api/financeiro/contas-pagar';

/** Serializa os filtros gerenciais, omitindo o que é padrão/vazio. */
function paramsGerencial(f: FiltroGerencialPagar) {
  return {
    pagina:          f.pagina ?? 1,
    tamanhoPagina:   f.tamanhoPagina ?? 20,
    busca:           f.busca && f.busca.trim() ? f.busca.trim() : undefined,
    fornecedorId:    f.fornecedorId || undefined,
    grupoDespesasId: f.grupoDespesasId || undefined,
    tipoDespesasId:  f.tipoDespesasId || undefined,
    centroCustoId:   f.centroCustoId || undefined,
    dtInicio:        f.dtInicio || undefined,
    dtFim:           f.dtFim || undefined,
    baseData:        f.baseData && f.baseData !== 'vencimento' ? f.baseData : undefined,
    situacao:        f.situacao && f.situacao !== 'todos' ? f.situacao : undefined,
    ordenarPor:      f.ordenarPor || undefined,
    direcao:         f.direcao || undefined,
  };
}

export const contasPagarService = {
  async getAll(filtro: FiltroContasPagar = {}): Promise<ListaFinanceiraResponse<ContaPagarResumoDto>> {
    const { pagina = 1, tamanhoPagina = 20, busca, status, dtInicio, dtFim, fornecedorId,
            ordenarPor, direcao } = filtro;
    const { data } = await apiClient.get<ListaFinanceiraResponse<ContaPagarResumoDto>>(BASE, {
      params: {
        pagina,
        tamanhoPagina,
        busca:        busca && busca.trim() ? busca.trim() : undefined,
        status:       status && status !== 'todos' ? status : undefined,
        dtInicio:     dtInicio || undefined,
        dtFim:        dtFim || undefined,
        fornecedorId: fornecedorId || undefined,
        // Ordenacao vai para o SERVIDOR: a lista e paginada la.
        ordenarPor:   ordenarPor || undefined,
        direcao:      direcao || undefined,
      },
    });
    return data;
  },

  async getById(id: number): Promise<ContaPagar> {
    const { data } = await apiClient.get<ContaPagar>(`${BASE}/${id}`);
    return data;
  },

  async getVencidas(): Promise<ContaPagarResumoDto[]> {
    const { data } = await apiClient.get<PaginadoResponse<ContaPagarResumoDto>>(`${BASE}/vencidas`);
    return data.itens ?? (data as unknown as ContaPagarResumoDto[]);
  },

  async getAVencer(dias = 30): Promise<ContaPagarResumoDto[]> {
    const { data } = await apiClient.get<PaginadoResponse<ContaPagarResumoDto>>(`${BASE}/a-vencer`, {
      params: { dias },
    });
    return data.itens ?? (data as unknown as ContaPagarResumoDto[]);
  },

  /**
   * Listagem da tela operacional: UMA LINHA POR PARCELA (paridade com o desktop).
   * Título legado sem parcela ainda aparece, com `semParcela: true`.
   */
  async getParcelas(
    filtro: FiltroContasPagar = {},
  ): Promise<ListaFinanceiraResponse<ContaPagarParcelaDto>> {
    const { pagina = 1, tamanhoPagina = 20, busca, status, dtInicio, dtFim, fornecedorId,
            ordenarPor, direcao, incluirVencidos } = filtro;
    const { data } = await apiClient.get<ListaFinanceiraResponse<ContaPagarParcelaDto>>(
      `${BASE}/parcelas`,
      {
        params: {
          pagina,
          tamanhoPagina,
          busca:        busca && busca.trim() ? busca.trim() : undefined,
          status:       status && status !== 'todos' ? status : undefined,
          dtInicio:     dtInicio || undefined,
          dtFim:        dtFim || undefined,
          incluirVencidos: incluirVencidos || undefined,
          fornecedorId: fornecedorId || undefined,
          ordenarPor:   ordenarPor || undefined,
          direcao:      direcao || undefined,
        },
      },
    );
    return data;
  },

  // ── Visão gerencial: fornecedor → títulos → parcelas ──────────────────

  /** Sintético: quanto se deve a CADA FORNECEDOR no recorte. */
  async getGerencialFornecedores(
    filtro: FiltroGerencialPagar = {},
  ): Promise<ListaFinanceiraResponse<ContaPagarFornecedorResumoDto>> {
    const { data } = await apiClient.get<ListaFinanceiraResponse<ContaPagarFornecedorResumoDto>>(
      `${BASE}/gerencial/fornecedores`, { params: paramsGerencial(filtro) });
    return data;
  },

  /** Analítico: como está cada TÍTULO (total / pago / saldo). */
  async getGerencialTitulos(
    filtro: FiltroGerencialPagar = {},
  ): Promise<ListaFinanceiraResponse<ContaPagarTituloGerencialDto>> {
    const { data } = await apiClient.get<ListaFinanceiraResponse<ContaPagarTituloGerencialDto>>(
      `${BASE}/gerencial/titulos`, { params: paramsGerencial(filtro) });
    return data;
  },

  /** Prazo médio de pagamento (dias entre emissão e pagamento). */
  async getPrazoMedio(filtro: FiltroGerencialPagar = {}): Promise<PrazoMedioPagamentoDto> {
    const { data } = await apiClient.get<PrazoMedioPagamentoDto>(
      `${BASE}/gerencial/prazo-medio`, { params: paramsGerencial(filtro) });
    return data;
  },

  async create(conta: CriarContaPagarRequest): Promise<ContaPagarDetalheDto> {
    const { data } = await apiClient.post<ContaPagarDetalheDto>(BASE, conta);
    return data;
  },

  async update(id: number, conta: AtualizarContaPagarRequest): Promise<ContaPagarDetalheDto> {
    const { data } = await apiClient.put<ContaPagarDetalheDto>(`${BASE}/${id}`, conta);
    return data;
  },

  async delete(id: number): Promise<void> {
    await apiClient.delete(`${BASE}/${id}`);
  },

  /** Exclui UMA parcela (o desktop também exclui por parcela). */
  async deleteParcela(parcelaId: number): Promise<void> {
    await apiClient.delete(`${BASE}/parcelas/${parcelaId}`);
  },

  /** Paga UMA parcela; `valorPago` menor que o saldo faz baixa parcial. */
  async baixarParcela(
    parcelaId: number, req: BaixarParcelaPagarRequest,
  ): Promise<BaixaParcelaResultado> {
    const { data } = await apiClient.put<BaixaParcelaResultado>(
      `${BASE}/parcelas/${parcelaId}/baixar`, req);
    return data;
  },

  async baixarParcelasLote(req: BaixarParcelasPagarLoteRequest): Promise<BaixaParcelaResultado[]> {
    const { data } = await apiClient.post<BaixaParcelaResultado[]>(
      `${BASE}/parcelas/baixar-lote`, req);
    return data;
  },

  async estornarParcela(parcelaId: number): Promise<BaixaParcelaResultado> {
    const { data } = await apiClient.put<BaixaParcelaResultado>(
      `${BASE}/parcelas/${parcelaId}/estorno`);
    return data;
  },

  async baixar(id: number, req: BaixarContaPagarRequest): Promise<BaixaResultado> {
    const { data } = await apiClient.put<BaixaResultado>(`${BASE}/${id}/baixar`, req);
    return data;
  },

  async baixarLote(req: BaixarLoteRequest): Promise<BaixaResultado[]> {
    const { data } = await apiClient.post<BaixaResultado[]>(`${BASE}/baixar-lote`, req);
    return data;
  },

  async estornar(id: number): Promise<BaixaResultado> {
    const { data } = await apiClient.put<BaixaResultado>(`${BASE}/${id}/estorno`);
    return data;
  },

  async getDetalhe(id: number): Promise<ContaPagarDetalheDto> {
    const { data } = await apiClient.get<ContaPagarDetalheDto>(`${BASE}/${id}/detalhe`);
    return data;
  },
};
